import React from "react";
import { FormControl, InputLabel, MenuItem, Select, FormHelperText } from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BackspaceIcon from "@mui/icons-material/Backspace";

// Constantes de configuración
const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

const BINARY_OPTIONS = {
  joined: [
    { value: "0", label: "Pendiente" },
    { value: "1", label: "Ingresado" },
  ],
  latest_link_status: [
    { value: "0", label: "Pendiente" },
    { value: "1", label: "Descargado" },
  ],
  notified: [
    { value: "0", label: "Pendiente" },
    { value: "1", label: "Notificado" },
  ],
};

const COMMON_FORM_STYLES = { left: "20px" };
const COMMON_FORM_SX = { m: 1, minWidth: 120 };

/**
 * Componente reutilizable para renderizar un FormControl con Select
 * @param {Object} props - Propiedades del componente
 */
const FilterSelect = ({ id, value, onChange, options, error, helperText, renderOption }) => (
  <FormControl style={COMMON_FORM_STYLES} variant="standard" sx={COMMON_FORM_SX} error={error}>
    <InputLabel id={`${id}-label`}>Selecciona..</InputLabel>
    <Select labelId={`${id}-label`} id={id} value={value} onChange={onChange}>
      <MenuItem value="">Ninguno</MenuItem>
      {options?.map(renderOption) || []}
    </Select>
    {error && <FormHelperText>{helperText}</FormHelperText>}
  </FormControl>
);

/**
 * Componente para renderizar filtros activos
 */
const ActiveFilters = ({ filters, onDelete }) => {
  if (filters.length === 0) return null;

  return (
    <div style={{ marginLeft: "20px" }}>
      {filters.map((item) => (
        <MDButton
          style={{ marginRight: "20px", marginBottom: "10px" }}
          endIcon={<BackspaceIcon />}
          variant="contained"
          color="error"
          size="medium"
          key={item.id}
          sx={{ m: 0 }}
          onClick={() => onDelete(item.id)}
        >
          {item.label}
        </MDButton>
      ))}
    </div>
  );
};

/**
 * Componente para el input de búsqueda de texto
 */
const SearchInput = ({ value, onChange, isValid, onAddFilter }) => (
  <>
    <MDInput
      error={!isValid}
      helperText={!isValid ? "Ingrese un valor." : ""}
      FormHelperTextProps={{ className: !isValid ? "Mui-error" : "" }}
      style={{ left: "20px", marginRight: "5px" }}
      required
      label="Buscar"
      onChange={onChange}
      value={value}
    />
  </>
);

/**
 * Componente para el selector de rango de fechas
 */
const DateRangeInput = ({
  dateFrom,
  dateUntil,
  onChangeDateFrom,
  onChangeDateUntil,
  isValid,
  onAddFilter,
}) => (
  <>
    <MDInput
      type="date"
      style={{ marginLeft: "20px" }}
      label="Desde"
      value={dateFrom}
      onChange={onChangeDateFrom}
      error={!isValid}
      helperText={!isValid ? "Ingresa una fecha en ambos campos." : ""}
      FormHelperTextProps={{ className: !isValid ? "Mui-error" : "" }}
    />
    <MDInput
      type="date"
      style={{ marginLeft: "25px" }}
      label="Hasta"
      value={dateUntil}
      onChange={onChangeDateUntil}
      error={!isValid}
      helperText={!isValid ? "Ingresa una fecha en ambos campos." : ""}
      FormHelperTextProps={{ className: !isValid ? "Mui-error" : "" }}
    />
  </>
);

/**
 * Componente para renderizar selectores de tipo
 */
const TypeSelectors = ({
  filter,
  search,
  handleFilterType,
  isFieldValid,
  dataTypeCard,
  dataRoles,
  dataStatus,
  dataSourceTypes,
  dataAttendance,
  handleAddFilter,
}) => {
  const renderDataOption = ({ id, name }) => (
    <MenuItem key={id} value={name}>
      {name}
    </MenuItem>
  );

  const renderStateOption = ({ id, state }) => (
    <MenuItem key={id} value={state}>
      {state}
    </MenuItem>
  );

  const renderAttendanceOption = ({ id, name }) => (
    <MenuItem key={id} value={id}>
      {name}
    </MenuItem>
  );

  const renderBloodTypeOption = (type, index) => (
    <MenuItem key={index} value={type}>
      {type}
    </MenuItem>
  );

  const renderBinaryOption = ({ value, label }) => (
    <MenuItem key={value} value={value}>
      {label}
    </MenuItem>
  );

  const getSelectConfig = () => {
    const configs = {
      type_carnet: {
        id: "select-type-carnet",
        options: dataTypeCard,
        renderOption: renderDataOption,
        helperText: "Tipo de carnet requerido",
      },
      card_type: {
        id: "select-card-type",
        options: dataTypeCard,
        renderOption: renderDataOption,
        helperText: "Tipo de carnet requerido",
      },
      role_id: {
        id: "select-role",
        options: dataRoles,
        renderOption: renderDataOption,
        helperText: "Tipo de rol requerido",
      },
      blood_type: {
        id: "select-blood-type",
        options: BLOOD_TYPES,
        renderOption: renderBloodTypeOption,
        helperText: "Tipo de sangre requerido",
      },
      status: {
        id: "select-status",
        options: dataStatus,
        renderOption: renderStateOption,
        helperText: "Estado requerido",
      },
      source_type_name: {
        id: "select-source-type",
        options: dataSourceTypes,
        renderOption: renderDataOption,
        helperText: "Tipo de fuente requerido",
      },
      attended: {
        id: "select-attended",
        options: dataAttendance,
        renderOption: renderAttendanceOption,
        helperText: "Estado de asistencia requerido",
      },
      joined: {
        id: "select-joined",
        options: BINARY_OPTIONS.joined,
        renderOption: renderBinaryOption,
        helperText: "Estado de ingreso requerido",
      },
      latest_link_status: {
        id: "select-link-status",
        options: BINARY_OPTIONS.latest_link_status,
        renderOption: renderBinaryOption,
        helperText: "Estado de descarga requerido",
      },
      notified: {
        id: "select-notified",
        options: BINARY_OPTIONS.notified,
        renderOption: renderBinaryOption,
        helperText: "Estado de notificación requerido",
      },
    };

    return configs[filter];
  };

  const config = getSelectConfig();

  if (!config) return null;

  return (
    <>
      <FilterSelect
        id={config.id}
        value={search}
        onChange={handleFilterType}
        options={config.options}
        error={!isFieldValid.search}
        helperText={config.helperText}
        renderOption={config.renderOption}
      />
    </>
  );
};

/**
 * Componente principal de filtros
 * Maneja la visualización y gestión de filtros para tablas de datos
 */
export default function Filter({
  dataFilter,
  filter,
  handleFilter,
  isVisible,
  handleChange,
  search,
  handleFilterType,
  isVisibleSelectDateInput,
  isVisibleType,
  dateFrom,
  dateUntil,
  handleChangeDateFrom,
  handleChangeDateUntil,
  handleClickSubmit,
  dataTypeCard,
  dataStatus,
  dataSourceTypes,
  handleAddFilter,
  filters,
  handleDeleteFilter,
  isFieldValid,
  dataRoles,
  dataAttendance,
}) {
  return (
    <>
      <ActiveFilters filters={filters} onDelete={handleDeleteFilter} />

      <div style={{ marginTop: "10px", display: "flex", alignItems: "center", flexWrap: "wrap" }}>
        <FormControl style={COMMON_FORM_STYLES} variant="standard" sx={COMMON_FORM_SX}>
          <InputLabel id="filter-select-label">Filtrado por...</InputLabel>
          <Select
            style={{ width: "auto" }}
            labelId="filter-select-label"
            id="filter-select"
            value={filter}
            label="Filtrado por..."
            onChange={handleFilter}
          >
            <MenuItem value="">NINGUNO</MenuItem>
            {dataFilter?.[0]?.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {item.field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isVisible && (
          <SearchInput
            value={search}
            onChange={handleChange}
            isValid={isFieldValid.search}
            onAddFilter={handleAddFilter}
          />
        )}

        {isVisibleSelectDateInput && (
          <DateRangeInput
            dateFrom={dateFrom}
            dateUntil={dateUntil}
            onChangeDateFrom={handleChangeDateFrom}
            onChangeDateUntil={handleChangeDateUntil}
            isValid={isFieldValid.date}
            onAddFilter={handleAddFilter}
          />
        )}

        {isVisibleType && (
          <TypeSelectors
            filter={filter}
            search={search}
            handleFilterType={handleFilterType}
            isFieldValid={isFieldValid}
            dataTypeCard={dataTypeCard}
            dataRoles={dataRoles}
            dataStatus={dataStatus}
            dataSourceTypes={dataSourceTypes}
            dataAttendance={dataAttendance}
            handleAddFilter={handleAddFilter}
          />
        )}
      </div>

      <MDButton
        style={{ left: "20px", marginTop: "10px" }}
        variant="gradient"
        color="info"
        onClick={handleClickSubmit}
      >
        Buscar
      </MDButton>
    </>
  );
}
